import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Filter,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Repeat,
  Upload,
  MoreHorizontal,
  Loader2
} from "lucide-react";
import axios from 'axios';
import { setupAxios } from '../../auth';
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { AccountsActionDropdown, DropdownPosition, getDropdownPosition } from './AccountsActionDropdown';
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

export default function AccountsSalesRecurringInvoices() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [invoices, setInvoices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);

  const filters = ["All", "Active", "Stopped", "Expired"];

  const fetchInvoices = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await api.get('/accounts/recurring-invoices');
      const data = response.data?.data || response.data || [];
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to fetch recurring invoices:', err);
      setError('Failed to load recurring invoices. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null);
      setDropdownPos(null);
    };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  const safeNumber = (val: any): number => {
    let num = Number(val);
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
      num = Number(clean);
      if (isNaN(num)) num = 0;
    }
    return num;
  };

  // Compute stats from live data
  const totalProfiles = invoices.length;
  const activeProfiles = invoices.filter(i => i.status?.toLowerCase() === 'active');
  const monthlyRevenue = activeProfiles.filter(i => i.frequency?.toLowerCase() === 'monthly').reduce((sum, i) => sum + safeNumber(i.total || 0), 0);
  const upcomingInvoices = activeProfiles.filter(i => {
    if (!i.nextInvoiceDate) return false;
    const nextDate = new Date(i.nextInvoiceDate);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7; // Upcoming in next 7 days
  }).length;

  // Note: Total generated would ideally come from backend history, using total profiles for now as proxy or 0
  const totalGenerated = invoices.reduce((sum, i) => sum + safeNumber(i.total || 0), 0);

  const formatCurrency = (val: any) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const stats = [
    { label: "Active Profiles", value: activeProfiles.length.toString(), change: "+0%", trend: "up", color: "blue" },
    { label: "Monthly Revenue", value: formatCurrency(monthlyRevenue), change: "+0%", trend: "up", color: "emerald" },
    { label: "Upcoming (7d)", value: upcomingInvoices.toString(), change: "0%", trend: "up", color: "amber" },
    { label: "Total Profile Value", value: formatCurrency(totalGenerated), change: "0%", trend: "up", color: "violet" },
  ];

  const filteredInvoices = invoices.filter(i => {
    if (activeFilter !== 'All' && i.status?.toLowerCase() !== activeFilter.toLowerCase()) return false;
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const matchProfile = i.profileName ? i.profileName.toLowerCase().includes(searchLower) : false;
    const matchCustomer = i.customerName ? i.customerName.toLowerCase().includes(searchLower) : false;
    const matchVessel = i.vesselName ? i.vesselName.toLowerCase().includes(searchLower) : false;
    const matchAccountCode = i.accountCode ? i.accountCode.toLowerCase().includes(searchLower) : false;
    return matchProfile || matchCustomer || matchVessel || matchAccountCode;
  });

  const handleDownloadCSV = () => {
    if (filteredInvoices.length === 0) {
      alert("No recurring invoice data to export.");
      return;
    }
    const headers = ["Sr. No.", "Profile Name", "Customer", "Vessel", "Account Code", "Frequency", "Next Invoice", "Amount", "Status"];
    const rows = filteredInvoices.map((invoice, index) => [
      index + 1,
      invoice.profileName || '-',
      invoice.customerName || '-',
      invoice.vesselName || invoice.vesselId || '-',
      invoice.accountCode || '-',
      invoice.frequency || 'Monthly',
      formatDate(invoice.nextInvoiceDate),
      formatCurrency(Number(invoice.total || 0)),
      invoice.status || 'Draft'
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `recurring_invoices_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'expired') return 'bg-red-100 text-red-700';
    if (s === 'stopped') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700'; // Draft
  };

  const invoice = activeDropdown !== null
    ? filteredInvoices.find((invoice) => invoice.id === activeDropdown)
    : null;

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">

        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">

          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5" id="main-scroll">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Accounts / Sales
                </div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">
                  Recurring Invoices
                </h1>
                <p className="text-xs text-slate-500">
                  Automate repetitive billing cycles
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Data refreshed just now</span>
                  <button onClick={fetchInvoices} className="p-1 rounded hover:bg-slate-100 transition-colors" title="Refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-blue-500' : ''}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.RECURRING_INVOICE_CREATE) && (
                  <button
                    onClick={() => navigate("/accounts/sales/recurring-invoices/new")}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Recurring
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl border border-red-100 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}>
                      <Repeat className={`w-4.5 h-4.5 text-${stat.color}-600`} />
                    </div>
                    <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${stat.trend === "up" ? "text-emerald-600" : "text-red-500"}`}>
                      {stat.trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {stat.change}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map((f) => (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${activeFilter === f ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search profiles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Filter className="w-4 h-4 text-slate-500" />
                  </button>
                  */}
                  <button
                    onClick={handleDownloadCSV}
                    className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto pb-24">
                <table className="w-full text-left min-w-175">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="px-4 py-3 font-medium text-center text-slate-400">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Profile Name</th>
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Vessel</th>
                      <th className="px-4 py-3 font-medium">Account Code</th>
                      <th className="px-4 py-3 font-medium">Frequency</th>
                      <th className="px-4 py-3 font-medium">Next Invoice</th>
                      <th className="px-4 py-3 font-medium text-right">Amount</th>
                      <th className="px-4 py-3 font-medium text-center">Status</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {isLoading ? (
                      <tr>
                        <td colSpan={10} className="p-16 text-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Loading recurring profiles...</p>
                        </td>
                      </tr>
                    ) : filteredInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={10}>
                          <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5">
                              <Repeat className="w-10 h-10" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                              {searchQuery ? 'No matching profiles' : 'No recurring invoices found'}
                            </h3>
                            <p className="text-slate-500 text-sm text-center max-w-sm mb-6">
                              {searchQuery ? 'Try adjusting your search or filters to find what you are looking for.' : 'Set up recurring profiles to auto-generate invoices on schedule.'}
                            </p>
                            {!searchQuery && (
                              <div className="flex items-center gap-3">
                                {hasPermission(ACCOUNTS_PERMS.RECURRING_INVOICE_CREATE) && (
                                  <button
                                    onClick={() => navigate("/accounts/sales/recurring-invoices/new")}
                                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                                  >
                                    <Plus className="w-4 h-4" />
                                    Create Profile
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredInvoices.map((invoice, index) => (
                        <tr key={invoice.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => hasPermission(ACCOUNTS_PERMS.RECURRING_INVOICE_UPDATE) && navigate(`/accounts/sales/recurring-invoices/${invoice.id}`)}>
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={e => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-blue-600 hover:underline">
                            {invoice.profileName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {invoice.customerName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-600 font-medium">
                            {invoice.vesselName || (invoice.vesselId ? `Vessel #${invoice.vesselId}` : '-')}
                          </td>
                          <td className="px-4 py-3">
                            {invoice.accountCode ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200/50">
                                {invoice.accountCode}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {invoice.frequency || 'Monthly'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDate(invoice.nextInvoiceDate)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700 text-right">
                            {formatCurrency(Number(invoice.total || 0))}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${getStatusBadge(invoice.status)}`}>
                              {invoice.status || 'Draft'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right relative">
                            <button
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100"
                              onClick={e => {
                                e.stopPropagation();
                                if (activeDropdown === invoice.id) {
                                  setActiveDropdown(null);
                                  setDropdownPos(null);
                                } else {
                                  setDropdownPos(getDropdownPosition(e.currentTarget as HTMLElement));
                                  setActiveDropdown(invoice.id);
                                }
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>

      {invoice && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
                                {hasPermission(ACCOUNTS_PERMS.RECURRING_INVOICE_UPDATE) && (
                                  <button
                                    className="w-full text-left px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                    onClick={() => {
                                      setActiveDropdown(null);
                                      setDropdownPos(null);
                                      navigate(`/accounts/sales/recurring-invoices/${invoice.id}`);
                                    }}
                                  >
                                    Edit Profile
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.RECURRING_INVOICE_DELETE) && (
                                  <>
                                    <div className="my-1 border-t border-slate-100"></div>
                                    <button
                                      className="w-full text-left px-4 py-2 text-[13px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                                      onClick={async () => {
                                        setActiveDropdown(null);
                                      setDropdownPos(null);
                                        if (window.confirm('Are you sure you want to delete this profile? This action cannot be undone.')) {
                                          try {
                                            await api.delete(`/accounts/recurring-invoices/${invoice.id}`);
                                            fetchInvoices();
                                          } catch (err: any) {
                                            console.error(err);
                                            alert('Failed to delete profile');
                                          }
                                        }
                                      }}
                                    >
                                      Delete Profile
                                    </button>
                                  </>
                                )}
        </AccountsActionDropdown>
      )}

    </>
  );
}
